
import { Heading, Button, IconButton, Spinner, Input, NativeSelect, Stack, VStack, HStack, Flex, Box, Field, Text, Separator, useBreakpointValue, Dialog, Portal, CloseButton } from '@chakra-ui/react'
import { LuTrash } from 'react-icons/lu'
import { useDocumentData } from '@kage1020/react-firebase-hooks/firestore';
import { db } from '../firebase'
import { doc } from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'
import { createAdminUserAccount, removeAdminUserRole } from '../services/firebaseService'
import { toaster } from './ui/toaster'
import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from '../i18n'



const SingleUser = ({ uid, role, canEdit, onRemove }) => {
    const { t } = useTranslation()
    const [user, loadingUser, errorUser] = useDocumentData(doc(db, 'users', uid));
    if (loadingUser) return <Text>{t('eventUsers.loadingUser')}</Text>
    if (errorUser) return <Text>{t('eventUsers.loadingError', { message: errorUser.message })}</Text>
    const displayName = user ? (user.name || user.email || uid) : uid
    return (<Flex
        key={uid}
        align="center"
        p={2}
        border="1px solid"
        borderColor="border.muted"
        borderRadius="md"
        gap={3}
    >

        <VStack align="flex-start" gap={0}>
            <Text fontWeight="semibold">{displayName}</Text>
            {user?.email && user?.name && <Text fontSize="sm" color="fg.subtle">{user.email}</Text>}
            <Text fontSize="sm" color="fg.muted">
                {role}
            </Text>
        </VStack>

        {canEdit && (
            <IconButton
                aria-label={t('common.remove')}
                size="xs"
                variant="ghost"
                colorPalette="red"
                ml="auto"
                onClick={() => onRemove(uid, displayName)}
            >
                <LuTrash />
            </IconButton>
        )}
    </Flex>
    )
}

export const EventUsers = ({ eventId, adminRole = 'eventadmin', typeLabel = '' }) => {
    const { isSysAdmin, isAdmin, eventRoles } = useAuth()
    const { t } = useTranslation()
    // sysadmins and this event's admin can add/remove users; moderators cannot.
    const canEdit = isSysAdmin || !!eventRoles?.find(r => r.eventid === eventId && r.role === 'eventadmin')
    // Guard the ref so the hook never runs against an empty eventId (doc() would throw).
    // The access / empty-state guards live after all hooks (below) so that hooks always
    // run in the same order on every render (react-hooks/rules-of-hooks).
    const [adminUsers, loadingAdminUsers, errorAdminUsers] = useDocumentData(eventId ? doc(db, "adminusers", eventId) : null);
    const isSpecialRole = adminRole !== 'eventadmin'
    const [newUserRole, setNewUserRole] = useState(isSpecialRole ? adminRole : 'moderator')
    const [newUserEmail, setNewUserEmail] = useState('')


    const [isAddingUser, setIsAddingUser] = useState(false);
    const [pendingUser, setPendingUser] = useState(null);
    const [userToRemove, setUserToRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    // Stack the add-user form (labels above fields, fields in a column) on mobile.
    const fieldOrientation = useBreakpointValue({ base: 'vertical', md: 'horizontal' })

    useEffect(() => {
        if (pendingUser && adminUsers && !isAddingUser) {
            const found = Object.values(adminUsers).length > 0 &&
                Object.keys(adminUsers).some(uid => uid !== 'id')
            if (found) setPendingUser(null)
        }
    }, [adminUsers, pendingUser, isAddingUser])

    const handleClick = useCallback(async () => {
        const email = newUserEmail;
        const role = newUserRole;
        try {
            setIsAddingUser(true);
            setPendingUser({ email, role });
            setNewUserEmail('');
            setNewUserRole(isSpecialRole ? adminRole : 'moderator');
            await createAdminUserAccount({
                email,
                role,
                eventId: eventId
            })
        } catch (err) {
            console.error(err);
            setPendingUser(null);
        } finally {
            setIsAddingUser(false);
        }
    }, [newUserEmail, newUserRole, eventId, isSpecialRole, adminRole]);

    const handleRemoveUser = useCallback((uid, displayName) => {
        setUserToRemove({ uid, displayName })
    }, []);

    const confirmRemoveUser = useCallback(async () => {
        if (!userToRemove) return
        try {
            setRemoving(true)
            await removeAdminUserRole(userToRemove.uid, eventId)
            toaster.create({ title: t('eventUsers.removed'), status: 'success' })
            setUserToRemove(null)
        } catch (err) {
            console.error(err)
            toaster.create({ title: t('eventUsers.removeFailed', { message: err.message }), status: 'error' })
        } finally {
            setRemoving(false)
        }
    }, [userToRemove, eventId, t]);


    if (!isAdmin) return
    if (!eventId) {
        return (<>
            <Separator />
            <Stack gap={3}>
                <Heading size="md">{t('eventUsers.title')}</Heading>
                <Text>{t('eventUsers.notCreatedYet')}</Text>
            </Stack>
        </>)
    }

    if (loadingAdminUsers) return (<VStack>
        <Text>{t('eventUsers.loadingAdmins')}</Text>
        <Spinner />
    </VStack>
    )
    if (errorAdminUsers) return <Text>{t('eventUsers.loadingAdminsError', { message: errorAdminUsers.message })}</Text>

    return (
        <>
        <Stack gap={3}>
            <Stack gap={2}>
                {adminUsers && Object.entries(adminUsers).map((u) => (
                    <SingleUser key={u[0]} uid={u[0]} role={u[1]} canEdit={canEdit} onRemove={handleRemoveUser} />
                ))}
                {pendingUser && (
                    <Flex align="center" p={2} border="1px solid" borderColor="border.muted" borderRadius="md" gap={3} opacity={0.6}>
                        <HStack>
                            <Spinner size="xs" />
                            <Text fontWeight="semibold">{pendingUser.email}</Text>
                            <Text fontSize="sm" color="fg.muted">{pendingUser.role}</Text>
                            <Text fontSize="xs" color="yellow.300">{t('eventUsers.adding')}</Text>
                        </HStack>
                    </Flex>
                )}
            </Stack>

            <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} gap={3} w="100%">
                <Field.Root orientation={fieldOrientation}>
                    <Field.Label>{t('eventUsers.addAdminUser')}</Field.Label>
                    <Input
                        size="sm"
                        placeholder={t('eventUsers.userEmail')}
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                    />

                    <Field.Label>{t('eventUsers.role')}</Field.Label>
                    <NativeSelect.Root size="sm" width={{ base: '100%', md: '240px' }}>
                        <NativeSelect.Field
                            bg="var(--mc-input-bg)"
                            value={newUserRole}
                            onChange={(e) => setNewUserRole(e.target.value)}
                        >
                            {isSpecialRole ? (
                                <option value={adminRole}>{adminRole === 'weddingadmin' ? t('eventUsers.weddingOrganizer') : t('eventUsers.typeOrganizer', { type: typeLabel })}</option>
                            ) : (
                                <>
                                    <option value="eventadmin" disabled={!isSysAdmin}>
                                        {t('eventUsers.eventAdmin')}
                                    </option>
                                    <option value="moderator">{t('eventUsers.moderator')}</option>
                                </>
                            )}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                    </NativeSelect.Root>
                </Field.Root>
                <Button
                    size="sm"
                    variant="outline"
                    w={{ base: '100%', md: 'auto' }}
                    onClick={handleClick}
                    loading={isAddingUser}
                    loadingText={t('eventUsers.adding')}
                    disabled={isAddingUser || !newUserEmail.trim()}
                >
                    {t('common.add')}
                </Button>
            </Flex>
        </Stack>

        {/* Remove user confirmation dialog */}
        <Dialog.Root open={!!userToRemove} onOpenChange={(e) => { if (!e.open && !removing) setUserToRemove(null) }} role="alertdialog">
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>{t('eventUsers.removeTitle')}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.Body>
                            <Text>{t('eventUsers.removeConfirm', { user: userToRemove?.displayName })}</Text>
                        </Dialog.Body>
                        <Dialog.Footer>
                            <Button variant="outline" disabled={removing} onClick={() => setUserToRemove(null)}>{t('common.cancel')}</Button>
                            <Button colorPalette="red" loading={removing} loadingText={t('eventUsers.removing')} onClick={confirmRemoveUser}>{t('common.remove')}</Button>
                        </Dialog.Footer>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" disabled={removing} />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
        </>
    )
}
